import type { CityConfig } from "@/pages/city-template/types";

export const portlandConfig: CityConfig = {
  city: "Portland",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "portland",
  route: "/cities/portland",
  title: "Emergency Passport Portland, OR | Fast Passport Renewal & Expedited Services",
  metaDescription: "Emergency passport and expedited passport services in Portland, OR — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Portland and metro Oregon travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Portland, Oregon",
  heroSubtitle: "Passport expired before your Europe trip? Flying out of PDX in days? Fast Passport Center helps Portland travelers get their passports fast — as fast as 24 hours. A+ BBB rated. No office visit required for most services.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Portland%20Oregon%20skyline%20at%20golden%20hour%20with%20Mount%20Hood%20visible%20in%20background%2C%20Willamette%20River%20reflecting%20warm%20amber%20light%2C%20iconic%20Hawthorne%20Bridge%20in%20foreground%2C%20lush%20Pacific%20Northwest%20greenery%20surrounding%20the%20city%2C%20dramatic%20clouds%20with%20soft%20sunset%20colors%2C%20photorealistic%20landscape%20photography%2C%20vibrant%20yet%20serene%20Pacific%20Northwest%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=portland-hero-01&orientation=landscape",
  heroImageAlt: "Portland Oregon skyline with Mount Hood and Willamette River at sunset",
  geoPosition: "45.5051;-122.6750",
  geoPlacename: "Portland, Oregon",
  lastModified: "2026-05-29",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "300 5th Ave, Suite 600, Seattle, WA 98104",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Portland", zips: "97201–97299" },
    { name: "Beaverton", zips: "97003–97078" },
    { name: "Hillsboro", zips: "97123–97124" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Tualatin", zips: "97062" },
    { name: "Sherwood", zips: "97140" },
    { name: "Milwaukie", zips: "97222, 97267" },
    { name: "Oregon City", zips: "97045" },
    { name: "West Linn", zips: "97068" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Troutdale", zips: "97060" },
    { name: "Fairview", zips: "97024" },
    { name: "Wood Village", zips: "97060" },
    { name: "Forest Grove", zips: "97116" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online", desc: "No office visit required to begin. Complete your order from home or work in Portland in minutes." },
    { icon: "ri-truck-line", title: "Ship Overnight from PDX Area", desc: "FedEx and UPS locations throughout Portland offer overnight shipping to our processing center." },
    { icon: "ri-shield-check-line", title: "Expert Document Review", desc: "Every application is reviewed by our team before submission to reduce errors and delays — especially important for last-minute passport needs." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Access", desc: "We hold reserved submission slots at the nearest regional passport agency — no public appointment hunting needed." },
  ],
  testimonials: [
    {
      name: "Marcus T.",
      location: "Portland, OR",
      rating: 5,
      text: "My passport expired three weeks before our Europe trip. Fast Passport Center got it renewed in 6 days — I didn't even have to leave my house except to ship the documents from a UPS store on Burnside. Absolutely incredible service.",
      service: "Passport Renewal",
    },
    {
      name: "Jasmine W.",
      location: "Beaverton, OR",
      rating: 5,
      text: "We needed a passport for our daughter before a school trip to Canada. Fast Passport Center walked us through the whole child passport process — even the part about getting both parents to sign off. We had it in 8 days. Super professional.",
      service: "Child Passport",
    },
    {
      name: "Derek L.",
      location: "Lake Oswego, OR",
      rating: 5,
      text: "Business trip to Tokyo announced on a Tuesday, passport expired. Fast Passport Center had me covered by Friday. The daily status updates were the most reassuring part — I always knew exactly where things stood.",
      service: "Emergency Passport",
    },
  ],
  facilities: [
    {
      id: "portland-pearl",
      name: "Portland Main Post Office — Pearl District",
      type: "USPS Passport Acceptance",
      address: "715 NW Hoyt St",
      city: "Portland",
      state: "OR",
      zip: "97208",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 9am–2pm",
      distance: "Pearl District, central Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Major passport acceptance location in central Portland. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-southeast",
      name: "Portland Southeast Station",
      type: "USPS Passport Acceptance",
      address: "7233 SE 47th Ave",
      city: "Portland",
      state: "OR",
      zip: "97206",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Southeast Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Southeast Portland passport acceptance. Confirm appointment and photo service availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-northeast",
      name: "Portland Alberta Station",
      type: "USPS Passport Acceptance",
      address: "4710 NE 26th Ave",
      city: "Portland",
      state: "OR",
      zip: "97211",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "NE Portland — Alberta Arts District",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Northeast Portland option near Alberta Arts District. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-northwest",
      name: "Portland Northwest Station",
      type: "USPS Passport Acceptance",
      address: "1020 NW 23rd Ave",
      city: "Portland",
      state: "OR",
      zip: "97210",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "NW Portland — Nob Hill",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Northwest Portland option. Confirm appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "multnomah-county",
      name: "Multnomah County Courthouse Clerk",
      type: "County Passport Acceptance",
      address: "1021 SW 4th Ave, Room 140",
      city: "Portland",
      state: "OR",
      zip: "97204",
      phone: "503-988-3020",
      hours: "Mon–Fri 8am–4pm (by appointment)",
      distance: "Downtown Portland — Government Center",
      website: "https://www.multco.us/county-clerk",
      websiteLabel: "Multnomah County Clerk",
      notes: "County clerk passport acceptance. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "beaverton-post",
      name: "Beaverton Post Office",
      type: "USPS Passport Acceptance",
      address: "4265 SW 106th Ave",
      city: "Beaverton",
      state: "OR",
      zip: "97005",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Beaverton — about 8 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Beaverton / Washington County option for western Portland metro residents.",
      icon: "ri-mail-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Portland before international travel?",
      a: "Yes. Through Fast Passport Center, Portland travelers can get an expedited passport in as little as 3–9 business days, and emergency processing is available in as fast as 24 hours depending on your eligibility and travel documentation. Start your order online today — don't wait until the last minute.",
    },
    {
      q: "How fast can I renew my passport in Portland?",
      a: "Fast passport renewal in Portland is available through Fast Passport Center in as little as 3 business days with our expedited service, or 3–5 weeks with our standard expedited option. Renewal applications are eligible if your current passport was issued at age 16 or older and within the last 15 years. No office visit required for most renewals.",
    },
    {
      q: "What if my passport expires before my trip to Europe?",
      a: "If your passport expires before your Europe trip, stop procrastinating and start now. European countries typically require your passport to be valid for at least 3–6 months beyond your travel dates. Fast Passport Center can expedite your passport renewal so you don't miss your flight. The sooner you start, the more options you have.",
    },
    {
      q: "Can I get a same-day passport appointment near Portland?",
      a: "Same-day passport appointments at the regional passport agency in Seattle are only available for documented life-or-death emergencies and are not guaranteed. For most urgent passport situations near Portland, Fast Passport Center is the faster and more predictable option — no Seattle drive required.",
    },
    {
      q: "Can I expedite a child passport in Portland?",
      a: "Yes. Child passport expediting in Portland is possible through Fast Passport Center, but child passports (for minors 15 and under) do require an in-person acceptance step with parental consent. We provide a clear checklist and instructions. Once the acceptance step is complete, we handle the expedited processing.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If your passport is lost or stolen before an upcoming trip, act immediately. You will need to complete Form DS-64 to report it lost or stolen and apply for a replacement. Fast Passport Center guides you through this process and can expedite your replacement passport. Don't delay — lost passport replacements take extra steps and lost passports cannot be used even if found later.",
    },
    {
      q: "Does Portland have a passport agency?",
      a: "Portland, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Portland residents.",
    },
    {
      q: "How long does a standard passport take in Portland?",
      a: "If you apply through a standard post office acceptance facility in Portland, processing times run 6–13 weeks — sometimes longer during peak travel season. If your travel is sooner than that, you need expedited processing. Fast Passport Center offers Portland travelers processing as fast as 24 hours.",
    },
    {
      q: "Can I get a passport renewal near me in Portland without visiting an office?",
      a: "Yes. Most Portland-area adults who are renewing a passport can do so entirely by mail — no in-person visit required. Start your order online with Fast Passport Center, complete your documents at home, and ship them overnight from any FedEx or UPS location in the Portland metro area.",
    },
    {
      q: "What airports do Portland passport holders typically fly from?",
      a: "Portland International Airport (PDX) is the primary airport for Portland travelers and offers direct international flights to Europe, Asia, and Canada. Salem (SLE) and Eugene (EUG) are regional options. If your passport isn't in hand, don't book international travel. Fast Passport Center can help you meet your deadline.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Pearl District", address: "811 NW 23rd Ave, Portland, OR 97210", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS — Downtown Portland", address: "908 SW 4th Ave, Portland, OR 97204", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — SW Portland", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM" },
    { name: "The UPS Store — NW Portland", address: "1500 NW Bethany Blvd, Beaverton, OR 97006", hours: "Mon–Fri 8 AM – 7 PM" },
    { name: "Walgreens — SE Hawthorne", address: "3534 SE Hawthorne Blvd, Portland, OR 97214", hours: "Daily 8 AM – 10 PM" },
    { name: "Walmart — Jantzen Beach", address: "1765 Jantzen Beach Ctr, Portland, OR 97217", hours: "Daily 7 AM – 11 PM" },
    { name: "Costco — Warehouse Road", address: "4849 NE 138th Ave, Portland, OR 97230", hours: "Mon–Fri 10 AM – 8:30 PM, Sat–Sun 9:30 AM – 6 PM" },
    { name: "CVS — NE Portland", address: "6320 NE Sandy Blvd, Portland, OR 97213", hours: "Daily 7 AM – 10 PM" },
    { name: "FedEx Office — Beaverton", address: "1590 NW 185th Ave, Beaverton, OR 97006", hours: "Mon–Fri 7 AM – 10 PM" },
    { name: "Walgreens — Lake Oswego", address: "525 SW Mcvey Ave, Lake Oswego, OR 97034", hours: "Daily 8 AM – 9 PM" },
  ],
  dmvLocations: [
    {
      name: "Portland Driver & Motor Vehicle Services — McLoughlin",
      address: "9301 SE McLoughlin Blvd, Portland, OR 97222",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Main Portland DMV office. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Portland Driver & Motor Vehicle Services — Swan Island",
      address: "1905 N Hayden Island Dr, Portland, OR 97217",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "North Portland DMV option. Appointment recommended.",
      featured: false,
    },
    {
      name: "Beaverton DMV Office",
      address: "14075 SW Barrows Rd, Beaverton, OR 97007",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Washington County option for west Portland metro residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "Primary international airport — 6 miles from downtown",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "PDX offers direct international flights to Amsterdam, London, Reykjavik, Tokyo, Vancouver, and more. The busiest travel seasons are May–August and December–January — plan passport needs well in advance.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "Alternative hub — about 175 miles north",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Portland travelers use SEA for additional flight options. Passport required for all international departures from either airport.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "Flying from PDX in 24–72 hours? Our emergency courier service is designed for urgent, last-minute travel from Portland and the metro area.",
      bullets: ["As fast as 24 hours", "Reserved agency submission slots", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already have a U.S. passport? We help Portland and metro Oregon travelers renew fast — passport expired before trip is one of our most common urgent requests.",
      bullets: ["Eligible for expedited processing", "Ship from any FedEx or UPS in Portland", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport before a trip? We guide Portland travelers through Form DS-64 and the replacement process — urgent timelines available.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Child passport expedited service for Portland families — minors under 16 require in-person acceptance and parental consent. We provide clear instructions and checklists.",
      bullets: ["Both parents must typically consent", "Clear checklist provided", "Expedited options after acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Portland residents — we guide you through the acceptance clerk process and handle expedited processing.",
      bullets: ["Step-by-step checklist", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Passport Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — we submit through reserved agency channels, not public lines. Same-day passport help for Portland travelers.",
      bullets: ["State Dept. registered courier", "Reserved daily submission slots", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Other Services",
      description: "Passport name changes after marriage or divorce, damaged passport replacement, second passports for frequent travelers — all handled with the same expedited courier access.",
      bullets: ["Name change after marriage/divorce", "Damaged passport replacement", "Second passport for frequent travel"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Portland, Oregon travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Expedited Passport", description: "Expedited passport processing for Portland residents needing passports faster than standard government mail timelines." },
    { name: "Passport Renewal", description: "Fast passport renewal for Portland, OR residents with a passport issued within the last 15 years at age 16 or older." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement service for Portland travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Portland families with minors under 16." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier passport submission service serving Portland, Oregon." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Waiting Too Long to Apply", description: "Portland travelers often assume 6–8 weeks is safe — it's not. Government processing has stretched to 10–14+ weeks during peak travel season. Apply as soon as you book your trip, especially for summer and holiday travel from PDX." },
    { icon: "ri-camera-off-line", title: "Wrong Passport Photo", description: "Photos from drugstores and photo booths often get rejected for lighting, background, or expression issues. We provide exact specs and can catch these issues before your documents are submitted." },
    { icon: "ri-file-damage-line", title: "Incomplete or Incorrect Forms", description: "One wrong date, one missing signature — your application gets sent back and you lose weeks. Our checklist catches errors before submission, critical for last-minute passport renewal in Portland." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Type", description: "Not all birth certificates are accepted. Some require a long-form certified copy with a raised seal. Fast Passport Center tells you exactly what you need based on your situation." },
    { icon: "ri-calendar-close-line", title: "Assuming the Post Office is Fast Enough", description: "Standard USPS passport processing out of Portland runs 6–13 weeks. If your trip to Europe, Japan, or Canada is in under 10 weeks, standard processing is a gamble. Use our courier service instead." },
    { icon: "ri-map-pin-time-line", title: "Name Mismatch Across Documents", description: "Your passport name must match your airline ticket exactly — especially for international flights out of PDX. Middle names, hyphens, and suffixes cause more rejections than you'd expect." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Portland travel date. Complete your secure order online — takes just a few minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We confirm your details and send you a personalized document checklist — exactly what you need, nothing more." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight from Portland", description: "Drop off at any FedEx or UPS in the Portland metro — most locations are open evenings and weekends. No office visit to our facility required." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit your application through our reserved passport agency slots — bypassing the public appointment system entirely." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport is Completed", description: "Your passport is completed within the timeframe you selected and shipped back to you. No guessing, no hoping." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — limited slots, often weeks out in Seattle", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Must drive to Seattle (3 hours each way)", fpc: "Ship documents overnight from Portland" },
    { feature: "Processing Speed", agency: "Urgent travel only — strict eligibility", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Courier Access", agency: "Not available to the public", fpc: "Reserved daily submission slots" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable", fpc: "Predictable processing based on option selected" },
    { feature: "Government Shutdowns", agency: "Operations may be impacted", fpc: "Not affected by government shutdowns" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Eugene", route: "/cities/eugene" },
    { name: "Fast passport renewal in Salem", route: "/cities/salem" },
    { name: "Emergency passport help near Beaverton", route: "#" },
    { name: "Rush passport service in Hillsboro", route: "#" },
    { name: "Fast passport renewal for Willamette Valley travelers", route: "/states/oregon" },
  ],
  fpcOfficeNote: "After signing up online, we send your personalized checklist and shipping instructions. Ship your documents overnight from any FedEx or UPS location in Portland or the metro area — most are open evenings. No office visit to our facility required for most services.",
  fpcOfficeDistance: "Serving Portland and the entire metro area remotely",
  serviceAreaIntro: "Whether you are in downtown Portland, Beaverton, Lake Oswego, Gresham, or anywhere in the greater Portland metro area, Fast Passport Center makes expedited passport processing simple — start online, ship overnight, and we handle the rest.",
  nearbyCitiesIntro: "We serve all Portland metro zip codes. Anywhere in Multnomah, Washington, Clackamas, or Clark County — ship overnight and your documents are at our processing center the next morning.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Portland as simple as possible. Most Portland clients never visit an office.",
  mistakesIntro: "These mistakes cause the most delays and rejections for Portland travelers. Fast Passport Center reviews your documents before submission to catch these issues before they become problems.",
  testimonialsIntro: "Real reviews from Portland, Beaverton, and Lake Oswego travelers who needed their passports fast and trusted Fast Passport Center.",
  faqIntro: "The most common questions Portland and metro Oregon travelers ask about emergency and expedited passport services.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
};